import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { MonthlyAddOnQuantities } from 'fym-shared/src/music24/monthlyAddOns';
import { useTRPCAPI, useTRPCClientAPI, useTRPCMutation, useTRPCQuery } from '../../../../trpc/trpc';
import { useUserToken } from '../../../../redux/slices/appInfo/hooks';
import { refreshSessionToken } from '../../../app/session/refreshSessionToken';

/** The values of one save: the two add-ons, and the extra artists of a team plan. */
export type MonthlyAddOnsSaveInput = MonthlyAddOnQuantities & { extraArtists?: number };

/**
 * The monthly add-ons of the active workspace (spec 2026-10-19): the read `billing.monthlyAddOns`
 * (every member) and the save `billing.setMonthlyAddOns` (the owner). The save answers the new
 * state; the read is fetched again after it. A save also refreshes the session token, so the
 * capabilities that the app reads from the token (the artist limit, the posts and the credits) show
 * the new limits at once.
 */
export function useMonthlyAddOns() {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const dispatch = useDispatch();
  const userToken = useUserToken();
  const query = useTRPCQuery(trpcAPI.billing.monthlyAddOns.queryOptions(undefined, { retry: false }));
  const { refetch: refetchUnsafe } = query;
  const refetch = useCallback(() => {
    refetchUnsafe().catch(console.error);
  }, [refetchUnsafe]);
  const refreshSession = useCallback(() => {
    if (userToken === null || userToken === undefined || userToken === '') {
      return;
    }
    refreshSessionToken(userToken, dispatch).catch(console.error);
  }, [userToken, dispatch]);
  const save = useTRPCMutation({
    mutationFn: (input: MonthlyAddOnsSaveInput) => trpcClient.billing.setMonthlyAddOns.mutate(input),
    onSuccess: refreshSession,
    onSettled: refetch,
  });
  return { data: query.data, isLoading: query.isLoading, error: query.error, refetch, save };
}
